import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, MenuItem, Select,
  FormControl, InputLabel, IconButton, Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import firebase from '../utilities/firebase';
import { StateManager } from '../utilities/stateManager';
import { useStackZIndex, ModalStackProvider, useSelectMenuProps } from '../utilities/ModalStackContext';

const EMPTY_FORM = {
  first_name: '', last_name: '', sex: '', birthday: '',
  email: '', phone_number: '',
  address1: '', city: '', state: '', zip: '',
  dl: '', routing_num: '', account_num: '',
};

const REQUIRED_FIELDS = ['first_name', 'last_name', 'email', 'phone_number', 'address1', 'city', 'state', 'zip'];

export default function NewCustomerModal({ open, onClose, stockNumber, type, initialName, onSaved, linkToDeal = true, heading }) {
  const myZ = useStackZIndex();
  const selectMenuProps = useSelectMenuProps(myZ);
  const [form, setForm] = React.useState(EMPTY_FORM);
  const [saving, setSaving] = React.useState(false);
  const [showErrors, setShowErrors] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      const parts = (initialName || '').trim().split(' ');
      setForm({
        ...EMPTY_FORM,
        first_name: parts[0] || '',
        last_name: parts.slice(1).join(' ') || '',
      });
      setShowErrors(false);
    }
  }, [open, initialName]);

  const set = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  const fieldError = (field) => showErrors && REQUIRED_FIELDS.includes(field) && !form[field];

  const handleSave = async () => {
    const missing = REQUIRED_FIELDS.find(f => !form[f]);
    if (missing) {
      setShowErrors(true);
      StateManager.setAlertAndOpen('Please fill in all required fields', 'error');
      return;
    }
    setSaving(true);
    try {
      const docRef = firebase.firestore().collection('customers').doc();
      const customerId = docRef.id;
      const customerData = { ...form, customer: customerId };
      if (stockNumber) customerData.stock = stockNumber;

      await docRef.set(customerData);

      if (linkToDeal) {
        await Promise.all([
          firebase.firestore().doc(`deals/${stockNumber}`).set({ [type]: customerId }, { merge: true }),
          firebase.firestore().doc(`cars/${stockNumber}`).set({ [type]: customerId }, { merge: true }),
        ]);
      }

      if (linkToDeal && type === 'consignor') {
        const name = `${form.first_name} ${form.last_name}`.trim();
        await firebase.firestore().doc(`purchases/${stockNumber}-NTO`).set({ vendor: name }, { merge: true });
      }

      const display_name = `${form.first_name}${form.last_name ? ' ' : ''}${form.last_name}`.trim();
      StateManager.setAlertAndOpen('Customer created', 'success');
      if (onSaved) onSaved({ ...customerData, id: customerId, display_name });
      onClose();
    } catch (err) {
      console.error(err);
      StateManager.setAlertAndOpen('Failed to create customer', 'error');
    }
    setSaving(false);
  };

  const typeLabel = type === 'consignor' ? 'Consignor' : type === 'cobuyer' ? 'Co-Buyer' : 'Buyer';

  return (
    <ModalStackProvider value={myZ}>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: myZ }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{heading || `New ${typeLabel}`}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="First Name *" value={form.first_name} onChange={set('first_name')} error={fieldError('first_name')} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Last Name *" value={form.last_name} onChange={set('last_name')} error={fieldError('last_name')} />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Sex</InputLabel>
              <Select value={form.sex} onChange={set('sex')} label="Sex" MenuProps={selectMenuProps}>
                {StateManager.sexes.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth size="small" label="Birthday" type="date"
              value={form.birthday} onChange={set('birthday')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Email *" type="email" value={form.email} onChange={set('email')} error={fieldError('email')} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Phone *" type="tel" value={form.phone_number} onChange={set('phone_number')} error={fieldError('phone_number')} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Address *" value={form.address1} onChange={set('address1')} error={fieldError('address1')} />
          </Grid>
          <Grid item xs={5}>
            <TextField fullWidth size="small" label="City *" value={form.city} onChange={set('city')} error={fieldError('city')} />
          </Grid>
          <Grid item xs={4}>
            <FormControl fullWidth size="small" error={fieldError('state')}>
              <InputLabel>State *</InputLabel>
              <Select value={form.state} onChange={set('state')} label="State *" MenuProps={selectMenuProps}>
                {StateManager.states.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <TextField fullWidth size="small" label="Zip *" value={form.zip} onChange={set('zip')} error={fieldError('zip')} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="License Number" value={form.dl} onChange={set('dl')} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Routing Number" value={form.routing_num} onChange={set('routing_num')} />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="Account Number" value={form.account_num} onChange={set('account_num')} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
    </ModalStackProvider>
  );
}
